'use client';

import { useSiteDataByKey } from '@/lib/hooks/useSiteData';
import Link from 'next/link';
import { useParams } from 'next/navigation';

const ResourcesSection = () => {
  const params = useParams();
  const currentLang = (params?.lang as string) || 'en';
  const { data: resourcesData, loading, error } = useSiteDataByKey('find-resources-for-you');

  // Helper to slugify text for URLs
  const slugify = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Helper to get link or generate default
  const getLinkOrDefault = (link: string | null | undefined, label: string) => {
    if (link && link !== '' && link !== '#' && link !== '/') {
      return link;
    }
    return `/${currentLang}/page/${slugify(label)}`;
  };

  // Show loading state
  if (loading) {
    return (
      <section className="resource-sec">
        <div className="container">
          <div className="row">
            <div className="col-lg-3 align-self-center">
              <div className="resource-info">
                <div className="placeholder-glow">
                  <h3 className="placeholder col-8 mb-3"></h3>
                  <p className="placeholder col-12"></p>
                  <p className="placeholder col-10"></p>
                </div>
              </div>
            </div>
            {Array.from({ length: 7 }).map((_, index) => (
              <div key={index} className="col-lg-3">
                <div className="resource-card">
                  <div className="card-img">
                    <div className="placeholder" style={{ width: '60px', height: '60px', borderRadius: '50%' }}></div>
                  </div>
                  <div className="card-conttent">
                    <h4 className="placeholder col-8 mb-2"></h4>
                    <p className="placeholder col-12 mb-3"></p>
                    <div className="card-btn">
                      <div className="placeholder" style={{ width: '40px', height: '40px', borderRadius: '50%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section className="resource-sec">
        <div className="container">
          <div className="row">
            <div className="col-lg-3 align-self-center">
              <div className="resource-info">
                <h3>Find <span>Resources</span> for You</h3>
                <p>Search for helpful resources to meet your needs. Find articles, books and guides, links, apps, and videos.</p>
              </div>
            </div>
            <div className="col-lg-9">
              <div className="alert alert-warning" role="alert">
                Unable to load resources. Please try again later.
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Extract data from API response
  const services = resourcesData?.data?.specifications?.find((spec: any) => spec.key === 'services')?.value || [];
  const title = resourcesData?.data?.title || 'Find Resources for You';
  const description = resourcesData?.data?.content ? 
    'Search for helpful resources to meet your needs. Find articles, books and guides, links, apps, and videos.' : 
    'Search for helpful resources to meet your needs. Find articles, books and guides, links, apps, and videos.';

  // Fallback static data if API fails
  const fallbackServices = [
    { name: 'Aging Well', description: 'Do you know the keys to healthy aging? Keep up with preventive care..', icons: '/images/icons/aging_icon.png', link: '#' },
    { name: 'Caregiving', description: 'Do you know the keys to healthy aging? Keep up with preventive care..', icons: '/images/icons/care_icon.png', link: '#' },
    { name: 'Community', description: 'Do you know the keys to healthy aging? Keep up with preventive care..', icons: '/images/icons/community_icon.png', link: '#' },
    { name: 'Financial', description: 'Do you know the keys to healthy aging? Keep up with preventive care..', icons: '/images/icons/financial_icon.png', link: '#' },
    { name: 'Housing', description: 'Do you know the keys to healthy aging? Keep up with preventive care..', icons: '/images/icons/house_icon.png', link: '#' },
    { name: 'Legal', description: 'Do you know the keys to healthy aging? Keep up with preventive care..', icons: '/images/icons/legal_icon.png', link: '#' },
    { name: 'Transportation', description: 'Do you know the keys to healthy aging? Keep up with preventive care..', icons: '/images/icons/transport_icon.png', link: '#' }
  ];

  const displayServices = services.length > 0 ? services : fallbackServices;

  return (
    <section className="resource-sec">
      <div className="container">
        <div className="row">
          <div className="col-lg-3 align-self-center">
            <div className="resource-info">
              <h3>Find <span>Resources</span> for You</h3>
              <p>{description}</p>
            </div>
          </div>
          {displayServices.map((service: any, index: number) => (
            <div key={service.name || index} className="col-lg-3">
              <div className="resource-card">
                <div className={`card-img ${service.name === 'Community' ? 'custom' : ''}`}>
                  <img 
                    src={service.icons || `/images/icons/${service.name.toLowerCase().replace(/\s+/g, '_')}_icon.png`} 
                    alt={service.name}
                    onError={(e) => {
                      // Fallback to local icon if API image fails
                      const target = e.target as HTMLImageElement;
                      target.src = `/images/icons/${service.name.toLowerCase().replace(/\s+/g, '_')}_icon.png`;
                    }}
                  />
                </div>
                <div className="card-conttent">
                  <h4>{service.name}</h4>
                  <p>{service.description}</p>
                  <div className="card-btn">
                    <Link href={getLinkOrDefault(service.link, service.name)} className="btn btn-secondary">
                      <img src="/images/icons/resource_btn.png" className="arrow-icon" alt="" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ResourcesSection;
